import { Component, Input, OnInit, signal } from '@angular/core';
import { Store } from '@ngneat/elf';
import { withUIEntities } from '@ngneat/elf-entities';
import { SelectItem } from 'primeng/api';
import { lastValueFrom } from 'rxjs';
import { CurrentAppIds } from 'src/app/core/components/user-admin-page/user-model';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { XyLinePlotUi } from 'src/app/core/models/xy-line-plot.model';
import { SessionStorageKeys, StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { MaxAllowableWearResultsModel } from 'src/app/perivis/models/max-allowable-wear.model';
import { SingleLoadResultsUi } from 'src/app/perivis/models/perivis-results-state.model';
import { StressResultsService } from 'src/app/perivis/services/stress-results.service';
import { PeriforOnChangeMessages, SignalRService } from 'src/app/shared/services/signal-r.service';

@Component({
  selector: 'app-casing-wear-results',
  templateUrl: './casing-wear-results.component.html',
  styles: [``],
  standalone: false
})
export class CasingWearResultsComponent implements OnInit {
  public casingWearResults: MaxAllowableWearResultsModel;
  public isLoading = signal<boolean>(true);
  public yAxisTitle = '';
  public xAxisTitle = 'Wear (%)';
  public tableName = 'Max Allowed Wear';
  public userUnits: UserUnitsModel;
  public plotName: string;
  public longLengthUnit: string;
  public signalRChange: boolean;
  public componentHeight: number;
  public columnDefs: { header: string, field: string }[];
  public columnDefinitions: any[];
  public errorMsg: string;
  public results: any[];
  public selectedResultDisplay: string = 'plot';
  public selectedDepthView: string = 'md';

  public loadCases: SelectItem[] = [];
  public resultsDisplay: SelectItem[] = [
    { label: 'Plot', value: 'plot' },
    { label: 'Grid', value: 'grid' }
  ];

  private selectedString: string;
  //State Management
  private _componentId: string;
  @Input() set componentId(value: string) {
    this._componentId = value;
    this.casingWearResultsStore = this._storeService.createStore(this.componentId, new SingleLoadResultsUi, withUIEntities<XyLinePlotUi>());
    this.selectedResultDisplay = this.casingWearResultsStore.state.selectedResultDisplay || 'plot';
    this.selectedDepthView = this.casingWearResultsStore.state.selectedDepthView || 'md';
  }
  get componentId(): string {
    return this._componentId;
  }
  public casingWearResultsStore: Store;

  constructor(
    private _stressResultsService: StressResultsService,
    private _signalRService: SignalRService,
    private _storeService: StoreService
  ) { }

  async ngOnInit(): Promise<void> {
    this.userUnits = await this._storeService.get<UserUnitsModel>(StorageKeys.UNITS);
    this.longLengthUnit = this.userUnits.longLengths;

    const appIds = this._storeService.getSession<CurrentAppIds>(SessionStorageKeys.APP_IDS);
    this.selectedString = appIds.tubularId;
    await this.refreshResults(true, false, false);

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));
  }

  private formatDecimal(value: any, divisor: number): string {
    return value || value === 0 ? (Math.trunc(value * divisor) / divisor).toLocaleString('en-US') : null;
  }

  async signalRfunc(data: any) {
    if (data.action == PeriforOnChangeMessages.REFRESH_RESULTS || data.action == PeriforOnChangeMessages.REFRESH_STRING_INPUTS) {
      const appIds = this._storeService.getSession<CurrentAppIds>(SessionStorageKeys.APP_IDS);
      this.selectedString = appIds.tubularId;
      await this.refreshResults(true, true, false);
    }
  }

  setPlotName(): string {
    return 'casingWearPlot - ' + this.selectedString;
  }

  private async refreshResults(isLoading: boolean, calledFromSignalR: boolean, calledFromMode: boolean) {
    this.isLoading.set(isLoading);

    this.yAxisTitle = `MD (${this.longLengthUnit})`;

    if ((!this.casingWearResults || calledFromSignalR) && !calledFromMode) {
      try {
        this.casingWearResults = await lastValueFrom(this._stressResultsService.getCasingWearResults());
        this.signalRChange = true;
      } catch {
        this.errorMsg = "Well Configuration Missing: Please specify at least one string";
        this.isLoading.set(false);
        return;
      }
    }

    if (!this.casingWearResults || (this.casingWearResults['maxAllowableWear'].length == 0)) {
      this.errorMsg = "Please calculate PERIVIS to see results";
      this.columnDefs = [];
      this.results = [];
      this.isLoading.set(false);
      return;
    } else {
      this.errorMsg = null;
    }

    this.handleSingleCasingWear(this.casingWearResults);
    this.plotName = this.setPlotName();
    this.isLoading.set(false);
  }

  private handleSingleCasingWear(casingWearResults: any) {
    this.results = [];

    this.columnDefs = [
      { header: 'Burst Wear', field: 'burstWear' },
      { header: 'Collapse Wear', field: 'collapseWear' },
      { header: 'Combined Wear', field: 'combinedWear' },
    ];

    this.columnDefinitions = [
      {
        header: `MD (${this.longLengthUnit})`,
        field: 'measuredDepth',
        valueFormatter: (params) => this.formatDecimal(params, 100),
      },
      {
        header: `TVD (${this.longLengthUnit})`,
        field: 'trueVerticalDepth',
        valueFormatter: (params) => this.formatDecimal(params, 100),
      },
      {
        header: `Burst Wear (%)`,
        field: 'burstWear',
        valueFormatter: (params) => this.formatDecimal(params, 100),
      },
      {
        header: `Collapse Wear (%)`,
        field: 'collapseWear',
        valueFormatter: (params) => this.formatDecimal(params, 100),
      },
      {
        header: `Combined Wear (%)`,
        field: 'combinedWear',
        valueFormatter: (params) => this.formatDecimal(params, 100),
      },
    ];

    this.results = casingWearResults.maxAllowableWear;

    this.loadCases = casingWearResults.maxAllowableWear.map((wearData, index) => ({
      label: `Load Case ${index + 1}: MD ${wearData.measuredDepth}, TVD ${wearData.trueVerticalDepth}`,
      value: wearData,
    }));

    if (
      !this.casingWearResultsStore.state.selectedLoadCase ||
      !this.loadCases.some((l) => l.value.tubularStringId == this.casingWearResultsStore.state.selectedLoadCase?.value.tubularStringId)
    ) {
      this.casingWearResultsStore.update((state) => ({
        ...state,
        selectedLoadCase: this.loadCases[0] ?? null,
      }));
    }
  }

  onSelectedResultDisplayUpdated(e): void {
    this.selectedResultDisplay = e.value;
    this.casingWearResultsStore.update(state => ({ ...state, selectedResultDisplay: e.value }));
  }
}
